import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Message, MessageQAType } from '../stores/messages_store';
import useStores from '../hooks/useStores';
import useClock from 'hooks/useClock';

export type MessagesContextType = {
    allMyMessages: Message[];
    sendAssociated: (
        identity: string,
        associated_type: string,
        associated_uuid: string,
        content: string,
    ) =>  Promise<Message|null>;

    fetchListByAssociation: (
        identity: string,
        associated_type: string,
        associated_uuid: string,
        cursor?: string,
    ) => Promise<Message[]>;

    getListByAssociation: (
        associated_type: string,
        associated_uuid: string,
    ) => Message[];

    fetchRecentMessages: (
        identity: string,
        associated_type: string,
        uuids: string[],
        cursor?: string | null
    ) => Promise<Message[]>;

    getMessagesForCollab: (collabKey: string) => Message[];
    qa: (workKey: string, collab: string, content: string) => Promise<MessageQAType|null>;
}

export const MessagesContext = React.createContext<MessagesContextType>({
    allMyMessages: [] as Message[],
    sendAssociated: () => { throw "Not implemented" },
    getMessagesForCollab: () => [] as Message[],
    fetchListByAssociation: () => { throw "Not implemented" },
    fetchRecentMessages: () => { throw "Not implemented" },
    getListByAssociation: () => [] as Message[],

    qa: () => { throw "Not implemented" }
});

export const MessagesProvider = observer(({ children }: { children: React.ReactNode }) => {

    const { messagesStore } = useStores();
    const { currentClock } = useClock();
    const [allMyMessages, setAllMyMessages] = useState([] as Message[]);

    useEffect(() =>  {
        setAllMyMessages(messagesStore.items)
        // console.log("All My Messages: %d", messagesStore.items.length);
    }, [setAllMyMessages, messagesStore.items]);

    // console.log("Current Clock: %o", currentClock);
    function getMessagesForCollab(collabKey: string) {
        return messagesStore.getMessages(collabKey) || [] as Message[];
    }

    function getListByAssociation(associated_type: string, associated_uuid: string) {
        return messagesStore
            .items
            .filter((m) =>
               m.associated_type === associated_type &&
                    m.associated_uuid === associated_uuid
            );
    }

    function qa(workKey: string, collab: string, content: string) {
        return messagesStore.qa(workKey, collab, content);
    }

    function sendAssociated(
        identity: string,
        associated_type: string,
        associated_uuid: string,
        content: string) {

            // set the context_type to the current clock's associated type
            let context_type = currentClock?.associated_type || '';
            let context_uuid = currentClock?.associated_uuid || '';
            // unless they're replying to the context itself
            if(associated_type === context_type &&
                associated_uuid === context_uuid) {
                context_type = '';
                context_uuid = '';
            }

        return messagesStore
            .sendAssociated(
                identity,
                associated_type,
                associated_uuid,
                content,
                context_type,
                context_uuid
            );
    }

    function fetchListByAssociation(identity: string, associated_type: string, associated_uuid: string, cursor?: string) {
        return messagesStore.fetchListByAssociation(
            identity,
            associated_type,
            associated_uuid,
            cursor || ""
        );
    }

    function fetchRecentMessages(
        identity: string,
        associated_type: string,
        uuids: string[],
        cursor?: string | null) {
        return messagesStore.fetchRecentMessages(identity, associated_type, uuids, cursor || '');
    }

    const data = {
        allMyMessages: allMyMessages,
        sendAssociated: sendAssociated,
        fetchListByAssociation: fetchListByAssociation,
        getMessagesForCollab: getMessagesForCollab,
        getListByAssociation: getListByAssociation,
        fetchRecentMessages: fetchRecentMessages,
        qa: qa
    };

    return (
        <MessagesContext.Provider value={data}>{ children }</MessagesContext.Provider>
    );

});
